import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  ListTodo, CheckCircle2, AlertTriangle, CalendarDays, CalendarClock,
  Clock, CircleSlash, BarChart3, ChevronLeft, Users
} from "lucide-react";

const NAVY = "#1a1a1a";
const SCARLET = "#ff4500";
const GREEN = "#1a8754";

const PRESETS = [
  { k: "today", label: "Today", days: 0 },
  { k: "7d", label: "Last 7 days", days: 6 },
  { k: "30d", label: "Last 30 days", days: 29 },
  { k: "90d", label: "Last 90 days", days: 89 },
];

function toISODate(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function MyAnalytics() {
  const { uid: paramUid } = useParams();
  const { user } = useAuth();
  const targetUid = paramUid || user?.id;
  const isSelf = !paramUid || paramUid === user?.id;

  const today = useMemo(() => new Date(), []);
  const [preset, setPreset] = useState("today");
  const [from, setFrom] = useState(toISODate(today));
  const [to, setTo] = useState(toISODate(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const applyPreset = (k) => {
    setPreset(k);
    const meta = PRESETS.find((p) => p.k === k);
    if (!meta) return;
    setFrom(toISODate(addDays(today, -meta.days)));
    setTo(toISODate(today));
  };

  const fetchData = useCallback(async () => {
    if (!targetUid) return;
    setLoading(true);
    try {
      const r = await api.get(`/users/${targetUid}/analytics`, { params: { frm: from, to } });
      setData(r.data); setErrorMsg("");
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [targetUid, from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (errorMsg) return (
    <div className="p-8">
      <div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-2xl p-6 max-w-xl" data-testid="analytics-error">
        <div className="font-display font-black text-lg">{errorMsg}</div>
        <div className="text-sm text-[#1a1a1a]/70 mt-1">Only the user themselves, Admins of a shared hub, or Controllers of the organization can view this person&apos;s analytics.</div>
        <Link to="/app" className="btn-pill btn-pill-secondary mt-3"><ChevronLeft className="w-3.5 h-3.5" /> Back to homepage</Link>
      </div>
    </div>
  );

  const c = data?.counters || {};
  const target = data?.user || {};

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <section className="hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden relative">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <BarChart3 className="w-3 h-3 text-[#ff5f15]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">
                {isSelf ? "Personal analytics" : `Analytics · ${target.org_role || "member"}`}
              </span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight" data-testid="analytics-heading">
              {isSelf ? "Your" : `${(target.name || "").split(" ")[0]}'s`} workload, by the numbers.
            </h1>
            <div className="mt-1 text-sm text-white/75">
              {target.name} · <span className="font-mono">{target.email}</span>
              {target.title && <> · {target.title}</>}
            </div>
          </div>
          {!isSelf && (
            <Link to="/app/admin" data-testid="analytics-back-admin" className="btn-pill btn-pill-ghost border border-white/25 self-start">
              <Users className="w-3.5 h-3.5" /> Back to members
            </Link>
          )}
        </div>
      </section>

      {/* Range picker */}
      <div className="surface-card rounded-2xl p-4 flex items-center gap-2 flex-wrap" data-testid="range-picker">
        {PRESETS.map((p) => (
          <button key={p.k} data-testid={`range-${p.k}`} onClick={() => applyPreset(p.k)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold rounded-full border transition-colors duration-150 ${
              preset === p.k ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/15 hover:border-[#1a1a1a]/40"
            }`}>
            {p.label}
          </button>
        ))}
        <div className="hidden sm:block w-px h-6 bg-[#1a1a1a]/10 mx-1" />
        <label className="font-mono text-[10px] uppercase tracking-wider text-[#1a1a1a]/55">Custom</label>
        <input data-testid="range-from" type="date" value={from}
          onChange={(e) => { setPreset("custom"); setFrom(e.target.value); }}
          className="bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
        <span className="text-[11px] text-[#1a1a1a]/55">→</span>
        <input data-testid="range-to" type="date" value={to}
          onChange={(e) => { setPreset("custom"); setTo(e.target.value); }}
          className="bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
        {loading && <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 ml-2">Loading…</span>}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
        <Kpi testId="kpi-todo" icon={ListTodo} label="To-do" value={c.to_do} />
        <Kpi testId="kpi-completed" icon={CheckCircle2} label="Completed" value={c.completed} tone={GREEN} />
        <Kpi testId="kpi-overdue" icon={AlertTriangle} label="Overdue" value={c.overdue} tone={SCARLET} />
        <Kpi testId="kpi-today" icon={CalendarDays} label="Due today" value={c.due_today} accent />
        <Kpi testId="kpi-tomorrow" icon={CalendarClock} label="Due tomorrow" value={c.due_tomorrow} />
        <Kpi testId="kpi-upcoming" icon={Clock} label="Upcoming" value={c.upcoming} />
        <Kpi testId="kpi-unscheduled" icon={CircleSlash} label="Unscheduled" value={c.unscheduled} />
      </div>

      {/* Graph */}
      <div className="surface-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Daily activity</div>
            <div className="font-display font-bold text-base">Due vs. completed per day</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-0.5" data-testid="range-label">
              {(data?.range?.from || from)} → {(data?.range?.to || to)}
            </div>
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }} data-testid="analytics-chart">
          <ResponsiveContainer minWidth={0} minHeight={0}>
            <LineChart data={data?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" stroke={NAVY} fontSize={10} tickFormatter={(d) => (d || "").slice(5)} />
              <YAxis stroke={NAVY} fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "white", border: `1px solid ${NAVY}`, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "ui-monospace, monospace" }} />
              <Line type="monotone" dataKey="due" name="Due" stroke={SCARLET} strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="completed" name="Completed" stroke={GREEN} strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
          Counts include Tasks (owned) + Actions (assigned). Due lines reflect items due on that date; Completed reflects items whose status moved to completed on that date.
        </div>
      </div>

      {/* By-kind & status mini-strips */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MiniBreakdown title="By status" testId="by-status" map={data?.by_status} palette={{ open: NAVY, in_progress: "#d6a900", completed: GREEN }} />
        <MiniBreakdown title="By type" testId="by-kind" map={data?.by_kind} palette={{ task: NAVY, action: SCARLET }} />
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, tone, testId }) {
  return (
    <div data-testid={testId} className={`surface-card rounded-2xl p-4 ${accent ? "ring-2 ring-[#ff4500]/30" : ""}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff4500] flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-display font-black text-3xl mt-1" style={{ color: tone || (accent ? SCARLET : NAVY) }}>{value ?? "—"}</div>
    </div>
  );
}

function MiniBreakdown({ title, map, palette, testId }) {
  const entries = Object.entries(map || {});
  const total = entries.reduce((s, [, v]) => s + (v || 0), 0) || 1;
  return (
    <div className="surface-card rounded-2xl p-5" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// {title}</div>
      <div className="font-display font-bold text-base mb-3">Distribution</div>
      {entries.length === 0 ? (
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">No data.</div>
      ) : (
        <ul className="space-y-2">
          {entries.map(([k, v]) => (
            <li key={k}>
              <div className="flex items-center justify-between text-[12px]">
                <span className="font-mono uppercase tracking-wider text-[#1a1a1a]/75">{k.replace("_", " ")}</span>
                <span className="font-display font-bold">{v}</span>
              </div>
              <div className="mt-1 h-1.5 bg-[#f7f5f2] rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${(v / total) * 100}%`, background: palette[k] || NAVY }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
